/* eslint-disable react/no-danger */
import React, { Suspense, useState, useEffect, useRef } from 'react';
import { AlertTitle, Avatar, List, ListItem, ListItemAvatar, ListItemText, Button, Box, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { addDays, addYears, format } from 'date-fns';
import DashboardOverview from '../startScreenComponents/DashboardOverview';
import Grid from '@mui/material/Grid';
import NewsFeed from '../startScreenComponents/NewsFeed';
import AuthAndAdminStatus from '../startScreenComponents/AuthAndAdminStatus';
import Charts from '../startScreenComponents/Charts';
import YouTubeCarousel from '../startScreenComponents/YoutubeCarousel';
import ContactSupport from '../startScreenComponents/ContactSupport';
import useTour from '../hooks/useTour';
import NpsDialog from '../npsDialog/NpsDialog';
import useSuspenseFetch from '../hooks/useSuspenseFetch';
import SplashScreen from '../splashScreen/SplashScreen';

const StartInner = () => {
  const { data: user } = useSuspenseFetch('user');
  const { data: accountInfo } = useSuspenseFetch('account');
  const { account } = user;
  const { smtpModified } = accountInfo;
  const hasCustomSmtpConfiguration = Boolean(accountInfo.smtpHost);
  const [alertMessages, setAlertMessages] = useState<{ severity: 'error' | 'warning' | 'info'; message: string }[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const { handleRunTour } = useTour();
  const [npsDialog, setNpsDialog] = useState(false);
  const [tourStarted, setTourStarted] = useState(false);
  const npsAutoOpenedRef = useRef(false);

  const handleDialogClose = () => {
    setOpenDialog(false);
    const expiryDate = new Date();
    expiryDate.setHours(23, 59, 59, 999);

    localStorage.setItem(`dialogDismissedExpiry_${account.id}`, expiryDate.getTime().toString());
  };

  const getGreeting = () => {
    const currentHour = new Date().getHours();

    if (currentHour < 12) {
      return 'Good Morning';
    }
    else if (currentHour < 18) {
      return 'Good Afternoon';
    }
    else {
      return 'Good Evening';
    }
  };

  const handleNpsDialogClose = () => {
    setNpsDialog(false);
  };

  useEffect(() => {
    if (user.showNpsSurvey && !npsAutoOpenedRef.current) {
      setNpsDialog(true);
      npsAutoOpenedRef.current = true;
      return;
    }

    if (!npsDialog && !user.userTours.includes('start-screen-tour') && !tourStarted) {
      handleRunTour({ path: 'start-screen-tour' });
      setTourStarted(true);
    }
  }, [user, npsDialog, handleRunTour, tourStarted]);

  useEffect(() => {
    let alerts = [];

    if (user.showNpsSurvey && npsDialog === false) {
      alerts.push({
        severity: 'warning',
        message: 'To help us improve your NewZapp experience, please take a moment to complete our latest NPS survey.'
      });
    }

    if (hasCustomSmtpConfiguration && smtpModified) {
      if (new Date() > addYears(new Date(smtpModified), 1)) {
        alerts.push({
          severity: 'warning',
          message: `Your Trusted Delivery SMTP password was set on ${format(new Date(smtpModified), 'dd/MM/yyyy')}. It may have expired.`
        });
      }
      else if (addDays(new Date(), 7) > addYears(new Date(smtpModified), 1)) {
        alerts.push({
          severity: 'warning',
          message: `Your Trusted Delivery SMTP password was set on ${format(new Date(smtpModified), 'dd/MM/yyyy')}. It may be due to expire soon.`
        });
      }
    }

    if (account.licence.lifetimeInMonths === 12) {
      const renewalDate = new Date(account.maxRenewalDate);
      const today = new Date();
      const timeDifference = renewalDate.getTime() - today.getTime();
      const daysLeft = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
      const lastDismissed = parseInt(localStorage.getItem(`dialogDismissedExpiry_${account.id}`) || '0');
      const shouldOpenDialog = new Date().getTime() > lastDismissed;

      if (daysLeft <= 1) {
        const message = 'Your current NewZapp licence is due to expire tomorrow. We have not yet received payment for your renewal. Without payment your account will not be accessible after today.';

        alerts.push({
          severity: 'error',
          message
        });

        setOpenDialog(shouldOpenDialog);
        setDialogMessage(message);
      }
      else if (daysLeft <= 7) {
        const message = `Your current NewZapp licence is due to expire on ${renewalDate.toDateString()}. We have not yet received payment for your renewal. Please ensure your renewal payment is received by your renewal date or you will not be able to access the platform.`;

        alerts.push({
          severity: 'error',
          message
        });

        setOpenDialog(shouldOpenDialog);
        setDialogMessage(message);
      }
      else if (daysLeft <= 14) {
        alerts.push({
          severity: 'warning',
          message: `Your current NewZapp licence is due to expire on ${renewalDate.toDateString()}. We have not yet received payment for your renewal. Please ensure your renewal payment is received by your renewal date or you will not be able to access the platform.`
        });
      }
      else if (daysLeft <= 30) {
        alerts.push({
          severity: 'warning',
          message: `Your current NewZapp licence is due to expire on ${renewalDate.toDateString()}, please ensure your renewal payment is received by this date.`
        });
      }
      else if (daysLeft <= 60) {
        alerts.push({
          severity: 'info',
          message: `Your current NewZapp licence is due to expire on ${renewalDate.toDateString()}, your account manager will contact you to discuss your requirements.`
        });
      }

      setAlertMessages(alerts);
    }
  }, [account, user]);

  return (
    <Box sx={{ px: 3, pb: 2 }}>
      {alertMessages.length > 0 && alertMessages.map(({ message, severity }, i) => (
        <Alert
          key={i}
          sx={{
            mt: 2
          }}
          variant="filled"
          severity={severity}
          action={
            user.showNpsSurvey && message.includes('NPS survey') && (
              <Button
                size="small"
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  setNpsDialog(true);
                }}
              >
                Leave Feedback
              </Button>
            )
          }
        >
          {user.showNpsSurvey && message.includes('NPS survey') && (
            <AlertTitle>Quarterly Customer Satisfaction feedback</AlertTitle>
          )}
          {message}
        </Alert>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <List sx={{ p: 0 }}>
          <ListItem>
            <ListItemAvatar>
              <Avatar
                sx={{
                  width: 50,
                  height: 50
                }}
                alt={user.name}
                src={user.avatarURL}
              />
            </ListItemAvatar>
            <ListItemText
              sx={{ ml: 1 }}
              primary={`${getGreeting()} ${user.name || ''}`}
              secondary={user?.emailAddress || ''}
              primaryTypographyProps={{ variant: 'h6' }}
              secondaryTypographyProps={{ variant: 'subtitle1' }}
            />
          </ListItem>
        </List>
      </Box>

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, xl: 8 }}>
          <>
            <Grid container columnSpacing={1}>
              <Grid size={{ xs: 12, md: 6 }} className="news-feed">
                <NewsFeed hasCustomSmtpConfiguration={hasCustomSmtpConfiguration} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <DashboardOverview />
              </Grid>
              <Grid size={{ xl: 12 }} sx={{ display: { xs: 'none', xl: 'block' } }} className="youtube-carousel">
                <YouTubeCarousel />
              </Grid>
            </Grid>
          </>
        </Grid>

        <Grid size={{ xs: 12, xl: 4 }}>
          <>
            <Grid container spacing={1}>
              <Grid size={{ xs: 12 }}>
                <AuthAndAdminStatus />
              </Grid>
              <Grid size={{ xs: 12 }} className="content-graph">
                <Charts />
              </Grid>
              <Grid size={{ xl: 12 }} sx={{ display: { xs: 'none', xl: 'block' } }} className="contact-support">
                <ContactSupport />
              </Grid>
            </Grid>
          </>
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', xl: 'none' } }} className="youtube-carousel">
          <YouTubeCarousel />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ display: { xs: 'block', xl: 'none' } }} className="contact-support">
          <ContactSupport />
        </Grid>
      </Grid>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Licence Expiry Warning</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
      {npsDialog && (
        <NpsDialog
          open={npsDialog}
          onClose={handleNpsDialogClose}
        />
      )}
    </Box>
  );
};

// todo the following is needed because React Router 7 requires each route to have its own Suspense boundary
// see https://github.com/remix-run/react-router/issues/10568
// inner component can be avoided by only loading resources where they are needed instead of drilling them like this

const Start = () => {
  return (
    <Suspense fallback={<SplashScreen />}>
      <StartInner />
    </Suspense>
  );
};

export default Start;